import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { node } from 'prop-types';
import { useLocation } from 'react-router-dom';

import featureToggle, { DAY_TRADING } from '@config/featureToggle';
import { API_SUCCESS_CODE, DEFAULT_GET_RESPONSE } from '@config/constanta';

import {
  getDtParamCashTransfer,
  getDtParamBuySell,
  getDtMultiplierList,
  getCheckStockDtEnabled,
} from '@services/api/stock/dayTrading/config';
import { getDtTradelimit } from '@services/api/stock/dayTrading/account';
import { getDtPortfolioList } from '@services/api/stock/dayTrading/portfolio';
import { postDtBuy, postDtSell } from '@services/api/stock/dayTrading/trading';
import { useQueryParams } from '@hooks/useQueryParams';
import stockService from '@services/stock';

export const DayTradingContext = React.createContext();

const DayTradingProvider = ({ children }) => {
  const isMounted = useRef(true);
  const { queryParams } = useQueryParams();
  const featDayTrading = featureToggle.some((feat) => feat.feature === DAY_TRADING && feat.toggle);
  const [dtPorto, setDTPorto] = useState(DEFAULT_GET_RESPONSE);
  const [loadingPorto, setLoadingPorto] = useState(true);
  const location = useLocation();
  const includeDtPorto =
    queryParams.get('tab') === 'dayTrading' || location.pathname.includes('/saham/');

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchDayTradingPortoList = useCallback(() => {
    // requested only if feature toggle true
    if (featDayTrading) {
      setLoadingPorto(true);
      getDtPortfolioList()
        .then((response) => {
          if (!isMounted.current) {
            return;
          }
          if (response?.err_code === API_SUCCESS_CODE) {
            setDTPorto(response);
          }
          setLoadingPorto(false);
        })
        .catch((err) => {
          console.error(err);
          setLoadingPorto(false);
        });
    }
  }, [featDayTrading, getDtPortfolioList]);

  const {
    isDtActive: dtActive,
    isDtBuySuspended: dtSuspendBuy,
    isDtSellSuspended: dtSuspendSell,
    isDtBuyTradingHourEnd: dtTradingHourEndBuy,
    isDtSellTradingHourEnd: dtTradingHourEndSell,
    isSuccess: loadingStatus,
    isDtInProcess: dtInProcess,
    refetch: fetchDTStatus,
  } = stockService.account.dt.status.useDtAccountStatus();

  const [loadingTrade, setLoadingTrade] = useState(false);
  const [dtTradeLimit, setDtTradeLimit] = useState({});
  const [isErrorFetchDTTradeLimit, setIsErrorFetchDTTradeLimit] = useState(false);

  const fetchDayTradingTradeLimit = useCallback(
    (code) => {
      if (featDayTrading && dtActive) {
        setLoadingTrade(true);
        getDtTradelimit(code)
          .then((response) => {
            if (!isMounted.current) {
              return;
            }
            setDtTradeLimit(response);
            setLoadingTrade(false);
            if (response?.err_code !== 'EC0000000') {
              setIsErrorFetchDTTradeLimit(true);
            }
          })
          .catch((err) => {
            console.error(err);
            setLoadingTrade(false);
            setIsErrorFetchDTTradeLimit(true);
          });
      }
    },
    [featDayTrading, dtActive, getDtTradelimit]
  );

  const [dtParamsCashTransfer, setDtParamsCashTransfer] = useState(false);
  const [dtParamsBuySell, setDtparamsBuySell] = useState({});

  const fetchDayTradingParamsCashTransfer = useCallback(() => {
    getDtParamCashTransfer()
      .then((response) => {
        if (!isMounted.current) {
          return;
        }
        setDtParamsCashTransfer(response?.result);
      })
      .catch((err) => console.error(err));
  }, [getDtParamCashTransfer]);

  const fetchDayTradingparamsBuySell = useCallback(() => {
    getDtParamBuySell()
      .then((response) => {
        if (!isMounted.current) {
          return;
        }
        setDtparamsBuySell(response?.result);
      })
      .catch((err) => console.error(err));
  }, [getDtParamBuySell]);

  useEffect(() => {
    let intervalPortoDt;
    // requested only if feature toggle true
    if (featDayTrading && dtActive) {
      fetchDayTradingPortoList();
    }
    if (featDayTrading && includeDtPorto && dtActive) {
      intervalPortoDt = setInterval(() => {
        fetchDayTradingPortoList();
      }, 5000);
    }
    return () => clearInterval(intervalPortoDt);
  }, [featDayTrading, includeDtPorto, dtActive]);

  useEffect(() => {
    if (featDayTrading) {
      fetchDTStatus();
    }
  }, [featDayTrading]);

  useEffect(() => {
    if (dtActive) {
      fetchDayTradingTradeLimit();
    }
  }, [dtActive]);

  // TODO: Remove when PopUpBuySell is deprecated
  const onBuyDayTradingStock = useCallback((payload) => postDtBuy(payload), [postDtBuy]);
  const onSellDayTradingStock = useCallback((payload) => postDtSell(payload), [postDtSell]);
  const checkStockDtEligibility = useCallback(
    (payload) => getCheckStockDtEnabled(payload),
    [getCheckStockDtEnabled]
  );

  const valueProps = useMemo(
    () => ({
      loadingStatus,
      loadingTrade,
      loadingPorto,
      dtActive,
      dtInProcess,
      dtSuspendBuy,
      dtSuspendSell,
      dtTradingHourEndBuy,
      dtTradingHourEndSell,
      dtPorto,
      fetchDTStatus,
      fetchDayTradingPortoList,
      dtTradeLimit,
      fetchDayTradingTradeLimit,
      dtParamsCashTransfer,
      fetchDayTradingParamsCashTransfer,
      dtParamsBuySell,
      fetchDayTradingparamsBuySell,
      onBuyDayTradingStock,
      onSellDayTradingStock,
      checkStockDtEligibility,
      isErrorFetchDTTradeLimit,
    }),
    [
      loadingStatus,
      loadingTrade,
      loadingPorto,
      dtActive,
      dtSuspendBuy,
      dtSuspendSell,
      dtTradingHourEndBuy,
      dtTradingHourEndSell,
      dtPorto,
      dtInProcess,
      fetchDTStatus,
      fetchDayTradingPortoList,
      dtTradeLimit,
      fetchDayTradingTradeLimit,
      dtParamsCashTransfer,
      fetchDayTradingParamsCashTransfer,
      dtParamsBuySell,
      fetchDayTradingparamsBuySell,
      onBuyDayTradingStock,
      onSellDayTradingStock,
      checkStockDtEligibility,
      isErrorFetchDTTradeLimit,
    ]
  );

  return <DayTradingContext.Provider value={valueProps}>{children}</DayTradingContext.Provider>;
};

DayTradingProvider.propTypes = {
  children: node.isRequired,
};

export default DayTradingProvider;
